import {
  FoodItem,
  FoodItemReturnType,
  PaginatedSearchResultsFoodItem,
  type PaginatedSearchResultsFoodItemKey,
} from "client/jspPlatformExperiment";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { isFoodItemArray } from "models/food/item/foodItem";
import {
  FoodTagEdit,
  withFoodTagToggled,
  withVariantReplaced,
} from "utils/food/foodTagToggleUtils";
import { FoodItemWithVariants } from "utils/food/foodVariantUtils";
import dayjs, { Dayjs } from "dayjs";
import { RootState } from "store/index";

interface FoodItemOptionsState {
  data: Record<number, FoodItem>;
  updatedAt: Dayjs | null | undefined;
  isLoading: boolean;
  isDownloading: boolean;
  error: string | null | undefined;
}

const initialState: FoodItemOptionsState = {
  data: {},
  updatedAt: null,
  isLoading: false,
  isDownloading: false,
  error: null,
};

const fetchAllFoodItemOptions = createAsyncThunk(
  "foodItemOptions/fetchAllFoodItemOptions",
  async (
    input:
      | {
          keywords?: string | null;
          number_per_page?: number | null;
          page_index?: number | null;
          return_keys_only?: boolean | null;
          only_procured_items?: boolean | null;
          only_updated_after?: string | null;
        }
      | null
      | undefined,
    { getState, signal, rejectWithValue },
  ) => {
    const onlyUpdatedAfter = (
      getState() as RootState
    ).foodItemOptions.updatedAt?.toISOString();
    return jspPlatformApi()
      .foodItem.getFoodItemSearch({
        query: {
          ...input,
          keywords: input?.keywords ?? null,
          return_keys_only: input?.return_keys_only ?? false,
          only_updated_after: onlyUpdatedAfter,
          // On the incremental refresh (updated-after-last-load) request
          // related foods so the few changed foods carry their
          // variant_food_items and equivalent_food_items. Without this the
          // incremental search returns plain foods and overwrites cached mains
          // with variant-less copies (e.g. a freshly-added recipe variant then
          // vanishes from the menu editing page). Skipped on the initial full
          // load — fetching related foods for the whole pool is far too heavy —
          // and when the caller only wants keys.
          return_type:
            onlyUpdatedAfter && !input?.return_keys_only
              ? FoodItemReturnType.FOOD_AND_RELATED_FOODS
              : undefined,
        },
        signal,
      })
      .then((res) => {
        const raw = res.data as PaginatedSearchResultsFoodItem | undefined;
        const data = raw?.data;
        if (data != null && isFoodItemArray(data)) {
          return data;
        }
        return null;
      })
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      });
  },
);

type DownloadType = "ingredient" | "recipe";

interface DownloadAllFoodItemsInput {
  downloadType: DownloadType;
  foodIds?: number[];
}

const downloadAllFoodItems = createAsyncThunk(
  "foodItemOptions/downloadAllFoodItems",
  async (input: DownloadAllFoodItemsInput, { rejectWithValue }) => {
    try {
      const { downloadType, foodIds: providedFoodIds } = input;
      let foodIds: number[] = providedFoodIds ?? [];

      // If foodIds are provided, use them; otherwise fetch all IDs
      if (foodIds.length === 0 && downloadType === "ingredient") {
        // Fetch all IDs by paginating through all results
        const pageSize = 500;
        let pageIndex = 0;
        let hasMore = true;

        while (hasMore) {
          // Sequential pagination is required - we need each page's total count before fetching the next
          let totalCount = 0;
          let pageFoodIds: number[] = [];

          // Use food item search API for ingredients (return_keys_only => PaginatedSearchResultsFoodItemKey)
          const pageResult =
            // eslint-disable-next-line no-await-in-loop
            await jspPlatformApi().foodItem.getFoodItemSearch({
              query: {
                keywords: null,
                number_per_page: pageSize,
                page_index: pageIndex,
                only_procured_items: true,
                return_keys_only: true,
              },
            });

          const { data: pageData, total_counts: totalCounts } =
            pageResult.data as PaginatedSearchResultsFoodItemKey;
          totalCount = totalCounts ?? 0;
          pageFoodIds = Array.isArray(pageData)
            ? pageData.map((item) => item.id)
            : [];

          foodIds = [...foodIds, ...pageFoodIds];

          const fetchedCount = (pageIndex + 1) * pageSize;
          hasMore = fetchedCount < totalCount;
          pageIndex++;
        }
      }

      // Download via generated client (responseType: "blob" set by post-openapi script; client passes it to axios)
      const res = await jspPlatformApi().foodItem.getFoodItemsDownload({
        query: { food_ids: foodIds },
      });
      return res.data as Blob;
    } catch (reason) {
      let message = "Unknown error!";
      if (isApiError(reason)) {
        const err = toJspApiError(reason);
        message = `[${err.status}] ${err.statusText}`;
        if (isErrorResponseBodyType(err.body)) {
          message = err.body.detail;
          if (err.status === 422) {
            message = JSON.stringify(err.body.detail);
          }
        }
      }
      return rejectWithValue(message);
    }
  },
);

const updateFoodItemOptions = (
  state: FoodItemOptionsState,
  action: PayloadAction<FoodItem[] | null>,
): FoodItemOptionsState => {
  return {
    ...state,
    updatedAt: dayjs().subtract(dayjs.duration({ minutes: 10 })),
    data: {
      ...state.data,
      ...action.payload?.reduce<Record<number, FoodItem>>(
        (acc, newFoodItem) => {
          acc[newFoodItem.id] = newFoodItem;
          return acc;
        },
        {},
      ),
    },
  };
};

/**
 * Merge food items into the cache WITHOUT advancing `updatedAt`. Use for
 * local merges of single API responses (e.g. a recipe save returning the
 * fresh main with its variants): the incremental-sync cutoff must only move
 * forward after a real fetch, or edits made by others since the last fetch
 * would be skipped on the next sync.
 */
const mergeFoodItemOptions = (
  state: FoodItemOptionsState,
  action: PayloadAction<FoodItem[] | null>,
): FoodItemOptionsState => {
  return {
    ...updateFoodItemOptions(state, action),
    updatedAt: state.updatedAt,
  };
};

/**
 * Patch tag links on cached foods after the server confirmed the edits.
 * Reads the CURRENT cache rather than a snapshot taken at click time, so
 * overlapping edits on one food (or on a main and its variant) never clobber
 * each other. A variant is patched inside its main's `variant_food_items`
 * (the tag board reads variant rows from there) and in its own top-level
 * entry when one is cached. Leaves `updatedAt` alone, like
 * `mergeFoodItemOptions`.
 */
const applyFoodTagEdits = (
  state: FoodItemOptionsState,
  action: PayloadAction<FoodTagEdit[]>,
): FoodItemOptionsState => {
  const data = { ...state.data };
  action.payload.forEach(({ foodId, mainFoodId, tag, isAdding }) => {
    if (data[foodId]) {
      data[foodId] = withFoodTagToggled({ food: data[foodId], tag, isAdding });
    }
    const main = data[mainFoodId] as FoodItemWithVariants | undefined;
    const nestedVariant =
      foodId !== mainFoodId
        ? main?.variant_food_items?.find((variant) => variant.id === foodId)
        : undefined;
    if (main && nestedVariant) {
      data[mainFoodId] = withVariantReplaced({
        main,
        variant: withFoodTagToggled({ food: nestedVariant, tag, isAdding }),
      });
    }
  });
  return { ...state, data };
};

export const foodItemOptionsSlice = createSlice({
  name: "foodItemOptionsSlice",
  initialState,
  reducers: {
    setFoodItemOptions: updateFoodItemOptions,
    mergeFoodItemOptions,
    applyFoodTagEdits,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllFoodItemOptions.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchAllFoodItemOptions.fulfilled, (state, action) => {
      return {
        ...state,
        ...updateFoodItemOptions(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchAllFoodItemOptions.rejected, (state, action) => {
      return {
        ...state,
        isLoading: false,
        error: action.payload as string,
      };
    });
    builder.addCase(downloadAllFoodItems.pending, (state) => {
      return { ...state, isDownloading: true };
    });
    builder.addCase(downloadAllFoodItems.fulfilled, (state) => {
      return { ...state, isDownloading: false };
    });
    builder.addCase(downloadAllFoodItems.rejected, (state, action) => {
      return {
        ...state,
        isDownloading: false,
        error: action.payload as string,
      };
    });
  },
});

export const actions = {
  ...foodItemOptionsSlice.actions,
  fetchAllFoodItemOptions,
  downloadAllFoodItems,
};

export default foodItemOptionsSlice.reducer;
